import { createElement, useEffect, useState, type ReactNode } from "react";
import type { ClientPresentationDocument, ClientPresentationNode } from "./domain";
import { getLocalFile } from "./services/local-file-store";

function PresentationImage({node}:{node:ClientPresentationNode}){
  const[url,setUrl]=useState("");
  useEffect(()=>{
    let active=true,current="";
    if(node.imageFileKey)getLocalFile(node.imageFileKey).then(file=>{
      if(file&&active){current=URL.createObjectURL(file);setUrl(current)}
    });
    return()=>{active=false;if(current)URL.revokeObjectURL(current)};
  },[node.imageFileKey]);
  return <figure className={`client-presentation-image layout-${(node.layout??"Automatic").toLowerCase().replaceAll(" ","-")}`}>
    {url?<img src={url} alt={node.alternativeText??node.title??""}/>:<div className="image-placeholder">Locally stored screenshot</div>}
    {(node.caption||node.text)&&<figcaption>{node.caption??node.text}</figcaption>}
  </figure>;
}

function children(node:ClientPresentationNode){
  return node.children?.map(item=><ClientPresentationNodeView node={item} key={item.id}/>);
}

function NodeText({value}:{value:string}){
  return <>{value.replace(/\r\n?/g,"\n").split(/\n{2,}/).map((part,index)=><p key={index}>{part}</p>)}</>;
}

export function ClientPresentationNodeView({node}:{node:ClientPresentationNode}):ReactNode{
  const tone=node.tone??"neutral",safeId=node.id.replace(/[^a-zA-Z0-9_-]/g,"-"),className=`client-presentation-node node-${node.type} tone-${tone} presentation-${safeId}`;
  if(node.type==="heading")return createElement(`h${node.level??3}`,{className},node.text??node.title);
  if(node.type==="paragraph")return <p className={className}>{node.text}</p>;
  if(node.type==="unordered-list")return <ul className={className}>{node.items?.map((item,index)=><li key={index}>{item}</li>)}</ul>;
  if(node.type==="ordered-list")return <ol className={className}>{node.items?.map((item,index)=><li key={index}>{item}</li>)}</ol>;
  if(node.type==="labelled-item")return <div className={className}><strong>{node.label??node.title}</strong>{node.url?<p><a href={node.url} target="_blank" rel="noopener noreferrer">{node.text??node.url}</a></p>:node.text?<NodeText value={node.text}/>:null}{node.note&&<small>{node.note}</small>}</div>;
  if(node.type==="metric")return <dl className={className}><div><dt>{node.label??node.title}</dt><dd>{node.value??node.text}</dd>{node.note&&<p>{node.note}</p>}</div></dl>;
  if(node.type==="screenshot")return <PresentationImage node={node}/>;
  if(node.type==="image-group")return <div className={className}>{node.title&&<h3>{node.title}</h3>}<div>{children(node)}</div></div>;
  if(node.type==="card-group")return <section className={className}>{node.title&&<h3>{node.title}</h3>}<div>{children(node)}</div></section>;
  if(node.type==="expandable-evidence")return <details className={className}><summary>{node.title??node.label??"View supporting evidence"}</summary><div>{node.text&&<p>{node.text}</p>}{children(node)}</div></details>;
  const Tag=node.type==="prompt-result"?"article":"section";
  return <Tag className={className}>{node.title&&<h3>{node.title}</h3>}{node.text&&<NodeText value={node.text}/>} {node.items?.length?<ul>{node.items.map((item,index)=><li key={index}>{item}</li>)}</ul>:null}{children(node)}</Tag>;
}

export function ClientPresentationRenderer({document}:{document:ClientPresentationDocument}){
  return <div className="client-presentation-document" data-presentation-version={document.schemaVersion}>
    {document.nodes.map(node=><ClientPresentationNodeView node={node} key={node.id}/>)}
  </div>;
}
